// Copyright 2026 Northern.tech AS
//
//    Licensed under the Apache License, Version 2.0 (the "License");
//    you may not use this file except in compliance with the License.
//    You may obtain a copy of the License at
//
//        http://www.apache.org/licenses/LICENSE-2.0
//
//    Unless required by applicable law or agreed to in writing, software
//    distributed under the License is distributed on an "AS IS" BASIS,
//    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//    See the License for the specific language governing permissions and
//    limitations under the License.
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { TextField, Typography } from '@mui/material';

import { ControlledSearch } from '@northern.tech/common-ui/Search';
import { ControlledAutoComplete } from '@northern.tech/common-ui/forms/Autocomplete';
import { ControlledCheckboxAutocomplete } from '@northern.tech/common-ui/forms/CheckboxAutocomplete';
import { Filters } from '@northern.tech/common-ui/forms/Filters';
import { getReleaseListState, getReleaseTags, getUpdateTypes as getUpdateTypesSelector } from '@northern.tech/store/selectors';
import { useAppDispatch } from '@northern.tech/store/store';
import { setReleasesListState } from '@northern.tech/store/thunks';
import pluralize from 'pluralize';

export const ReleasesFilters = ({ classes }: { classes: Record<string, string> }) => {
  const { selectedTags = [], searchTerm = '', searchTotal, total, type } = useSelector(getReleaseListState);
  const existingTags = useSelector(getReleaseTags);
  const updateTypes = useSelector(getUpdateTypesSelector);
  const dispatch = useAppDispatch();

  const searchUpdated = useCallback(searchTerm => dispatch(setReleasesListState({ searchTerm })), [dispatch]);

  const onFiltersChange = useCallback(({ name, tags, type }) => dispatch(setReleasesListState({ selectedTags: tags, searchTerm: name, type })), [dispatch]);

  return (
    <>
      <Filters
        className={classes.container}
        onChange={onFiltersChange}
        initialValues={{ name: searchTerm, tags: selectedTags, type }}
        defaultValues={{ name: '', tags: [], type: '' }}
        filters={[
          {
            key: 'name',
            title: 'Release name',
            Component: ControlledSearch,
            componentProps: {
              onSearch: searchUpdated,
              placeholder: 'Starts with'
            }
          },
          {
            key: 'tags',
            title: 'Tags',
            Component: ControlledCheckboxAutocomplete,
            componentProps: {
              chipDisplay: true,
              options: existingTags,
              placeholder: 'Select tags'
            }
          },
          {
            key: 'type',
            title: 'Contains Artifact type',
            Component: ControlledAutoComplete,
            componentProps: {
              autoHighlight: true,
              freeSolo: true,
              options: updateTypes,
              renderInput: params => <TextField {...params} placeholder="Any" />
            }
          }
        ]}
      />
      <Typography variant="caption" className={classes.searchNote}>
        {searchTerm && searchTotal !== total ? `Filtered from ${total} ${pluralize('Release', total)}` : ''}
      </Typography>
    </>
  );
};

export default ReleasesFilters;
